import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Spin} from 'antd';
import {Bar, Doughnut} from 'react-chartjs-2';
import {toast} from 'react-toastify';
import {GET_ASSET_CLASS_AND_DABAPRO_DATA} from '../resolvers';
import {
  Card,
  ChartFrame,
  EmptyState,
  Section,
  ValueLegend,
} from '../../shared/DashboardUI';
import DateRangeFilter, {describeRange} from '../../shared/DateRangeFilter';
import {baseOptions, plainNumber, seriesColor} from '../../shared/chartTheme';
import styles from '../../shared/dashboard.module.scss';

/**
 * What people subscribe to, and what they hold.
 *
 * Both charts previously carried a Chart.js `title` repeating the `<h2>`
 * directly above them, and a Chart.js legend that named the colours without
 * naming a single number — so a doughnut showing three asset classes could tell
 * you that stocks were the biggest slice but not by how much or out of what.
 * The legend is now a list with the counts and the shares in it, which is the
 * part someone came for; the ring is there to show the proportion at a glance.
 */

const titleCase = value =>
  (value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());

const toItems = rows =>
  (rows ?? []).map((row, index) => ({
    label: titleCase(row?.label),
    value: Number(row?.value) || 0,
    color: seriesColor(index),
  }));

/**
 * `assetClass` and `dabaPro` share a {label, value} shape but not a unit:
 * the asset-class values are already percentages summing to 100, while the
 * plan values are subscription counts. Rendering both the same way printed
 * "38.46" beside a computed "38%" — the same fact twice, in two notations.
 */
const DistributionCard = ({title, description, items, emptyTitle, asShare}) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);

  const data = useMemo(
    () => ({
      labels: items.map(item => item.label),
      datasets: [
        {
          data: items.map(item => item.value),
          backgroundColor: items.map(item => item.color),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    }),
    [items],
  );

  const options = useMemo(
    () => ({
      ...baseOptions(),
      cutout: '62%',
      scales: {},
      plugins: {
        legend: {display: false},
        tooltip: {
          ...baseOptions().plugins.tooltip,
          callbacks: {
            label: context =>
              asShare
                ? ` ${context.label}: ${context.parsed.toFixed(1)}%`
                : ` ${context.label}: ${plainNumber(context.parsed)}${
                    total > 0
                      ? ` (${Math.round((context.parsed * 100) / total)}%)`
                      : ''
                  }`,
          },
        },
      },
    }),
    [total, asShare],
  );

  return (
    <Card>
      <div className={styles.sectionHead}>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        {!asShare && (
          <span className={styles.sectionMeta}>{plainNumber(total)} total</span>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState title={emptyTitle} compact />
      ) : (
        <div className={styles.donutLayout}>
          <ChartFrame height={200}>
            <Doughnut data={data} options={options} />
          </ChartFrame>
          <ValueLegend
            items={items.map(item => ({
              ...item,
              display: asShare
                ? `${item.value.toFixed(1)}%`
                : plainNumber(item.value),
            }))}
            total={asShare ? 0 : total}
          />
        </div>
      )}
    </Card>
  );
};

const PlanCard = ({items}) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);

  const data = useMemo(
    () => ({
      labels: items.map(item => item.label),
      datasets: [
        {
          label: 'Subscriptions',
          data: items.map(item => item.value),
          backgroundColor: items.map(item => item.color),
          borderRadius: 6,
          maxBarThickness: 54,
        },
      ],
    }),
    [items],
  );

  const options = useMemo(() => {
    const base = baseOptions({integer: true, valueLabel: 'Subscriptions'});
    return {
      ...base,
      scales: {
        ...base.scales,
        y: {...base.scales.y, suggestedMax: 4},
      },
    };
  }, []);

  return (
    <Card>
      <div className={styles.sectionHead}>
        <div>
          <h3>Daba Pro plans</h3>
          <p>Subscriptions taken out, by plan length</p>
        </div>
        <span className={styles.sectionMeta}>{plainNumber(total)} total</span>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No subscriptions in this period" compact />
      ) : (
        <ChartFrame height={230}>
          <Bar data={data} options={options} />
        </ChartFrame>
      )}
    </Card>
  );
};

export const AssetClassAndDabaProTab = () => {
  const [range, setRange] = useState({startDate: null, endDate: null});

  const {data, loading} = useQuery(GET_ASSET_CLASS_AND_DABAPRO_DATA, {
    variables: {filters: range},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: () => toast.error('Could not load subscription and asset data'),
  });

  const payload = data?.getAssetClassAndDabaProChartData?.data;
  const assetItems = toItems(payload?.assetClass);
  const planItems = toItems(payload?.dabaPro);

  return (
    <div className={styles.page} style={{padding: 0}}>
      <DateRangeFilter value={range} onChange={setRange} loading={loading} />

      <Section
        title="Distribution"
        description="What subscriptions were sold and where invested value sits."
        meta={describeRange(range)}>
        {loading ? (
          <div className={styles.loading}>
            <Spin />
            <span>Loading distribution…</span>
          </div>
        ) : (
          <div className={styles.split}>
            <DistributionCard
              title="Asset classes"
              description="Share of invested value by class"
              items={assetItems}
              emptyTitle="No holdings in this period"
              asShare
            />
            <PlanCard items={planItems} />
          </div>
        )}
      </Section>
    </div>
  );
};

export default AssetClassAndDabaProTab;
